import { useState, useEffect } from 'react';
import { useGym } from '../../contexts/GymContext';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import BoardEditor from './BoardEditor';
import AdminHeader from './AdminHeader';

const BoardManagement = () => {
  console.log('[BoardManagement] Component mounted/rendered');
  const { selectedGym } = useGym();
  console.log('[BoardManagement] selectedGym from context:', selectedGym);

  const [boards, setBoards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddBoard, setShowAddBoard] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    console.log('[BoardManagement] useEffect triggered, selectedGym:', selectedGym);
    if (selectedGym) {
      console.log('[BoardManagement] selectedGym exists, calling loadBoards');
      loadBoards();
    } else {
      console.log('[BoardManagement] selectedGym is null/undefined, NOT calling loadBoards');
    }
  }, [selectedGym]);

  const loadBoards = async () => {
    try {
      setIsLoading(true);
      console.log('[BoardManagement] loadBoards called, selectedGym:', selectedGym);
      console.log('[BoardManagement] selectedGym.gymId:', selectedGym?.gymId);

      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/boards/gym/${selectedGym.gymId}/manage`;
      console.log('[BoardManagement] Fetching from:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('[BoardManagement] Response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to load boards');
      }

      const data = await response.json();
      console.log('[BoardManagement] Loaded boards:', data);
      setBoards(data);
    } catch (error) {
      console.error('Error loading boards:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getBoardTypeBadge = (type) => {
    const colors = {
      tension: 'bg-gray-300 text-gray-900',
      moon: 'bg-blue-100 text-blue-800',
      kilter: 'bg-green-100 text-green-800',
      decoy: 'bg-orange-100 text-orange-800',
      grasshopper: 'bg-yellow-100 text-yellow-800',
      spray: 'bg-purple-100 text-purple-800',
      other: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[type] || colors.other}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { color: 'bg-green-100 text-green-800', icon: '🟢', label: 'Active' },
      maintenance: { color: 'bg-yellow-100 text-yellow-800', icon: '🔧', label: 'Maintenance' },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: '⚫', label: 'Inactive' }
    };

    const { color, icon, label } = config[status] || config.inactive;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        <span className="mr-1">{icon}</span>
        {label}
      </span>
    );
  };

  const formatAngle = (board) => {
    if (board.is_adjustable_angle) {
      return `${board.min_angle}° - ${board.max_angle}°`;
    }
    return board.angle ? `${board.angle}°` : '—';
  };

  const getFeaturesList = (board) => {
    const features = [];

    if (board.features?.led) features.push('LED');
    if (board.features?.fan) features.push('Fan');
    if (board.features?.mirrored) features.push('Mirrored');
    if (board.hold_sets && board.hold_sets.length > 0) {
      features.push(...board.hold_sets);
    }

    return features.length > 0 ? features.join(', ') : '—';
  };

  const handleEdit = (board) => {
    setSelectedBoard(board);
    setShowAddBoard(true);
  };

  const handleAddNew = () => {
    setSelectedBoard(null);
    setShowAddBoard(true);
  };

  const handleDelete = async (boardId) => {
    const board = boards.find(b => b.id === boardId);
    const confirmMessage = `⚠️  IMPORTANT: Archive "${board?.name}"?

This board may have a QR code physically mounted in your gym.

When you archive this board:
✓ The board will be hidden from members
✓ All existing reservations will remain accessible
✓ The QR code will STILL WORK if you reactivate this board later

⚠️  NEVER delete this board from the system if you have printed QR codes!

To restore this board later, use the "Show Inactive Boards" filter.

Are you sure you want to archive this board?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/boards/${boardId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ status: 'inactive' })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to archive board');
      }

      loadBoards();
    } catch (error) {
      console.error('Error archiving board:', error);
      alert('Failed to archive board');
    }
  };

  const handleActivate = async (boardId) => {
    const board = boards.find(b => b.id === boardId);
    const confirmMessage = `Activate "${board?.name}"?

This will make the board visible to members again and allow new reservations.

Continue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/boards/${boardId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ status: 'active' })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to activate board');
      }

      loadBoards();
    } catch (error) {
      console.error('Error activating board:', error);
      alert('Failed to activate board');
    }
  };

  // Filter boards based on showInactive toggle
  const filteredBoards = showInactive
    ? boards
    : boards.filter(b => b.status !== 'inactive');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading boards..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <AdminHeader
            title="Board Management"
            description={`Manage climbing boards, attributes, and reservation settings for ${selectedGym?.name}`}
          >
            <div className="mt-4 flex gap-3">
              <Button onClick={handleAddNew}>
                + Add New Board
              </Button>
              <button
                onClick={() => setShowInactive(!showInactive)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showInactive
                    ? 'bg-gray-700 text-white hover:bg-gray-800'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {showInactive ? 'Hide Inactive' : 'Show Inactive'}
              </button>
            </div>
          </AdminHeader>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Board List */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {filteredBoards.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No boards found</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by adding a new board.</p>
                <div className="mt-6">
                  <Button onClick={handleAddNew}>
                    + Add New Board
                  </Button>
                </div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Board Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type & Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Angle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Features
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active Reservations
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBoards.map((board) => (
                    <tr key={board.id} className={board.is_featured ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">
                                {board.name}
                              </div>
                              {board.is_featured && (
                                <span className="text-xs text-blue-600">⭐ Featured</span>
                              )}
                              {board.show_new_badge && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  NEW
                                </span>
                              )}
                            </div>
                            {board.location_notes && (
                              <div className="text-xs text-gray-500 mt-1">
                                📍 {board.location_notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {getBoardTypeBadge(board.type)}
                          <div>{getStatusBadge(board.status)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatAngle(board)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {getFeaturesList(board)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {board.active_reservations_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(board)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => window.open(`/admin/board-qr-codes?board=${board.id}`, '_blank')}
                            className="text-green-600 hover:text-green-900"
                          >
                            QR
                          </button>
                          {board.status === 'inactive' ? (
                            <button
                              onClick={() => handleActivate(board.id)}
                              className="text-green-600 hover:text-green-900 font-medium"
                            >
                              Activate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDelete(board.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Summary Stats */}
          {boards.length > 0 && (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Total Boards</dt>
                        <dd className="text-lg font-semibold text-gray-900">{boards.length}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Active</dt>
                        <dd className="text-lg font-semibold text-gray-900">
                          {boards.filter(b => b.status === 'active').length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Maintenance</dt>
                        <dd className="text-lg font-semibold text-gray-900">
                          {boards.filter(b => b.status === 'maintenance').length}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Current Reservations</dt>
                        <dd className="text-lg font-semibold text-gray-900">
                          {boards.reduce((sum, b) => sum + (b.active_reservations_count || 0), 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Board Modal */}
      {showAddBoard && (
        <BoardEditor
          board={selectedBoard}
          gymId={selectedGym.gymId}
          onClose={() => {
            setShowAddBoard(false);
            setSelectedBoard(null);
          }}
          onSave={(savedBoard) => {
            setShowAddBoard(false);
            setSelectedBoard(null);
            loadBoards();
          }}
        />
      )}
    </div>
  );
};

export default BoardManagement;
